/**
 * JSON 卡片校验/修复器（组件渲染校验修复器）
 *
 * 解耦设计：引入即用，不引入则不用。
 *
 * 用法
 *   1. 在 main.js 中执行 import '@/utils/jsonCardChecker'
 *   2. import 后会自动注册 validateJsonCard 到 window.__JSON_CARD_CHECKER__
 *   3. CosmoClawMessageItem 检测到 window.__JSON_CARD_CHECKER__ 存在后，
 *      会对组件 JSON 卡片先校验再渲染（不引入则走原渲染逻辑）
 *
 * 调用 POST /agent-api/claw/api/v1/json-card/check
 *   - 请求体 { cardJson: "<原始 JSON 卡片文本>" }
 *   - 响应  { code:200, data: { valid, cardJson, repairAttempts, errorMessage } }
 *   - valid=true  -> cardJson 即为可渲染的最终 JSON（可能已被修复）
 *   - valid=false -> cardJson 为原始输入，前端降级处理
 *
 * 接口定义在 @/api/agentAuth（checkJsonCardApi，走 /agent-api/claw 统一拦截器）
 */
import { checkJsonCardApi } from '@/api/agentAuth'

/**
 * 校验并修复 JSON 卡片
 * @param {string} cardJson 原始 JSON 卡片文本（可为非法 JSON、带 ```json 围栏、空串等）
 * @returns {Promise<{valid:boolean, cardJson:string, repairAttempts:number, errorMessage:string|null, fallback?:boolean}>}
 *   - 正常情况以服务端 data.valid 为准
 *   - 网络/服务异常时降级返回 { valid:true, cardJson:原文, fallback:true }，
 *     保证不阻断渲染（回退本地原有解析逻辑）
 */
export async function validateJsonCard(cardJson) {
  const input = cardJson === undefined || cardJson === null ? '' : String(cardJson)
  const fallback = {
    valid: true,
    cardJson: input,
    repairAttempts: 0,
    errorMessage: '校验服务不可用，已按原始内容渲染',
    fallback: true
  }
  try {
    const res = await checkJsonCardApi(input)
    const body = res && res.data
    // 兼容包裹结构 { code, msg, data: {...} }，优先取 data 字段
    const data =
      body && body.data && typeof body.data === 'object' && 'valid' in body.data
        ? body.data
        : body
    if (!data || typeof data !== 'object' || typeof data.valid !== 'boolean') {
      return fallback
    }
    return {
      valid: data.valid,
      cardJson: typeof data.cardJson === 'string' ? data.cardJson : input,
      repairAttempts: Number(data.repairAttempts) || 0,
      errorMessage: data.errorMessage || null
    }
  } catch (e) {
    // 网络异常/服务不可用时降级，不阻断组件渲染
    return fallback
  }
}

// 注册到全局，供 CosmoClawMessageItem 检测启用（解耦：不引入则 window 上无此函数）
if (typeof window !== 'undefined') {
  window.__JSON_CARD_CHECKER__ = validateJsonCard
}

export default validateJsonCard
